import sumBy from 'lodash/sumBy';

import {
  AggregatedSeries,
  FlatSegment,
  FlatSortingOptions,
  InsightsQueryResults,
  SortBy,
  SortOrder,
} from 'common/report/util/insights/types';
import {
  getFlatSegments,
  getSingleNumberFromSegment,
  getSortedFlatSegments,
  getSortedKeyMap,
} from 'common/report/util/insights/utils';
import {ChartDataItem} from 'common/charts/visualization/pie-chart/types';
import {SegmentFilterController, SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {OTHER_SEGMENT_VALUE} from 'common/report/util/insights/constants';

export interface PieChartData {
  pieSeries: ChartDataItem[]; // sorted array of pie series for use in the visualization component
  legendSegmentItems: SegmentItem[]; // sorted array of info for each segment (for use in SegmentFilterController)

  // Insights uses a special index with a stable sort order (independent from the bookmark sort order) to store selected legend segments
  legendIdToIndexMap: {[id: string]: number};
  legendIndexToId: string[];

  // easy lookups for data segment to present tooltips or an aggregate "Other" segment
  segmentLookup: Map<string, FlatSegment>; // Map key is FlatSegment.key, same as SegmentItem.id in `legendSegmentItems`
}

interface OtherSegment {
  metricKeys: string[];
  totalValue: number;
}

export function getOtherSegment(
  segmentLookup: Map<string, FlatSegment>,
  segmentFilterController: SegmentFilterController,
): OtherSegment {
  const unselectedSegments: FlatSegment[] = Array.from(segmentLookup.values()).filter((segment) => {
    return !segmentFilterController.isItemSelected(0, segment.key);
  });
  const metricKeys = unselectedSegments.map((segment: FlatSegment) => segment.path[0]);
  const totalValue = sumBy(unselectedSegments, (segment: FlatSegment) => getSingleNumberFromSegment(segment));

  return {metricKeys, totalValue};
}

export function getPieChartData(results: InsightsQueryResults): PieChartData {
  // flatten results into convenient segments
  const segments = getFlatSegments(results.headers, results.series);

  // create lookups based on segment key, useful for presenting tooltips or an aggregate "Other" segment
  const segmentLookup = new Map(segments.map((segment) => [segment.key, segment]));

  // always sort segments by value (it's not a user config)
  const pieSortConfig: FlatSortingOptions = {
    sortBy: SortBy.Value,
    sortOrder: SortOrder.Descending,
  };
  const sortedSegments = getSortedFlatSegments(segments, pieSortConfig);

  // generate line chart series & legend info from segments
  const pieSeries: ChartDataItem[] = [];
  const legendSegmentItems: SegmentItem[] = [];
  sortedSegments.forEach((segment, idx) => {
    const segmentColor = getChartSegmentColorCombo(idx).primary;
    const textColor = getChartSegmentColorCombo(idx).secondaryColor;
    pieSeries.push({
      color: segmentColor,
      name: segment.key,
      displayName: segment.displayName,
      secondaryColor: textColor,
      value: getSingleNumberFromSegment(segment),
    });
    legendSegmentItems.push({id: segment.key, label: segment.displayName, color: segmentColor});
  });

  // generate special indices used by Insights to store selected legend segments
  const aggregatedSeries: AggregatedSeries = segments.reduce((aggregate: AggregatedSeries, segment) => {
    aggregate[segment.key] = {
      paths: [segment.path],
      total: getSingleNumberFromSegment(segment),
    };
    return aggregate;
  }, {});
  const legendIdToIndexMap = getSortedKeyMap(aggregatedSeries);
  const legendIndexToId: string[] = [];
  Object.keys(legendIdToIndexMap).forEach((id) => (legendIndexToId[legendIdToIndexMap[id]] = id));

  if (legendSegmentItems.length > 1) {
    legendSegmentItems.unshift({
      id: OTHER_SEGMENT_VALUE,
      label: `Other`,
      color: `none`,
    });
  }

  return {
    pieSeries,
    legendSegmentItems,
    legendIdToIndexMap,
    legendIndexToId,
    segmentLookup,
  };
}
